/**
 * Shared layout primitives for the merchant-detail page.
 *
 * Extracted from `AdminMerchantDetail.jsx` in Brief 3.5 PR 6.1 — pure
 * mechanical refactor, zero behavior change. PR 7's lint gate runs
 * cleanly only when the surface area of any one file stays small.
 */
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

export function Section({ icon: Icon, title, subtitle, action, testId, children }) {
  return (
    <section data-testid={testId} className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-6">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="inline-flex items-center gap-2.5">
          <Icon className="h-4 w-4 text-zinc-500" />
          <h2 className="font-display text-[18px] tracking-tight text-white">{title}</h2>
          {subtitle && <span className="text-[12px] text-zinc-500">{subtitle}</span>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function CollapsibleSection({ icon: Icon, title, testId, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section data-testid={testId} className="rounded-2xl border border-white/[0.06] bg-white/[0.015]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-toggle`}
        className="w-full flex items-center justify-between gap-3 p-6 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className="inline-flex items-center gap-2.5">
          <Icon className="h-4 w-4 text-zinc-500" />
          <h2 className="font-display text-[18px] tracking-tight text-white">{title}</h2>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRight className="h-4 w-4 text-zinc-500" />}
      </button>
      {open && <div className="px-6 pb-6">{children}</div>}
    </section>
  );
}

export function SegmentedFilter({ value, options, onChange, testId }) {
  return (
    <div className="inline-flex gap-1 rounded-full border border-white/[0.06] bg-white/[0.015] p-1" data-testid={testId}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          data-testid={`${testId}-${o.key}`}
          className={`px-3 py-1 rounded-full text-[11.5px] transition-colors ${
            value === o.key ? "bg-white/[0.08] text-white" : "text-zinc-400 hover:text-zinc-200"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export function fmtRelative(iso) {
  if (!iso) return null;
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return "just now";
    if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86400_000) return `${Math.floor(ms / 3600_000)}h ago`;
    return `${Math.floor(ms / 86400_000)}d ago`;
  } catch { return iso; }
}

/** Small label / value pair. */
export function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="text-zinc-200 mt-0.5 truncate">{String(value)}</div>
    </div>
  );
}
